<template>
  <div>
    <div class="users-trainings-show">
      <ShowFormItemWrapper title="名前">
        <div
          class="d-flex flex-wrap align-items-center justify-content-between w-100"
        >
          <span>{{ training.title }}</span>
          <div
            class="d-flex flex-wrap align-items-center"
            style="font-size: 0.8rem"
          >
            <span class="me-3"
              >公開日：{{ basicFormatter(training.publishedAt) }}
            </span>
            <span class="me-3"
              >公開終了日：{{
                training.unpublishedAt
                  ? basicFormatter(training.unpublishedAt)
                  : "-"
              }}
            </span>
          </div>
        </div>
      </ShowFormItemWrapper>
      <ShowFormItemWrapper title="説明">
        <span class="show-white-space">{{ training.remarks }}</span>
      </ShowFormItemWrapper>
      <ShowFormItemWrapper v-if="training.attachedUrl" title="リンク">
        <Inplace v-if="!training.unpublished">
          <template #display>
            <i class="pi pi-video"></i>
            動画を再生する
          </template>
          <template #content>
            <p class="text-danger mb-3">
              ※この動画コンテンツは著作権法により保護されています。<br />
              無断での複製・転載・改変・配布・放送・上映・公開等を固く禁じます。<br />
              違反が判明した場合、法的措置を取る可能性があります。<br />
            </p>
            <vue-plyr ref="plyr" :options="playerOptions" @ready="onReady">
              <video ref="videoRef" controls crossorigin playsinline>
                <source :src="training.attachedUrl" type="video/mp4" />
              </video>
            </vue-plyr>
          </template>
        </Inplace>
        <p v-else class="text-danger">この動画は公開終了しました</p>
      </ShowFormItemWrapper>
      <ShowFormItemWrapper
        v-if="training.watchedUsers.length"
        title="閲覧したメンバー"
      >
        <div class="d-flex flex-column">
          <Dropdown
            v-model="selectedOrganizationId"
            :options="training.organizations"
            option-label="name"
            option-value="id"
            style="min-width: 150px"
            class="radius mb-3"
          />
          <ul class="d-flex flex-column flex-wrap">
            <li
              v-for="u in training.watchedUsers.filter((u) =>
                u.organizations.find((o) => o.id === selectedOrganizationId)
              )"
              :key="`${u.id}-id`"
              class="ms-4"
            >
              {{ u.fullName }}({{
                u.organizations.map((o) => o.name).join(",")
              }})
            </li>
          </ul>
        </div>
      </ShowFormItemWrapper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import { playerOptions } from "/@/modules/plyr";
import { ShowFormItemWrapper } from "/@/vue/components/Molecules/Form";
import { ShowAttachedPdfs } from "/@/vue/components/Molecules/PDF";
import { TrainingClient } from "/@/types";
import { basicFormatter } from "/@/modules/luxon";
import Inplace from "primevue/inplace";
import Dropdown from "primevue/dropdown";

const props = defineProps<{
  training: TrainingClient;
}>();

const emit = defineEmits<{
  (e: "markAsWatched", id: number): void;
  (e: "sendWatchedMinutes", minutes: number): void;
}>();

const alreadyMarkedWatched = ref(false);
let intervalId: ReturnType<typeof setInterval> | null = null;
async function markTrainingAsWatched() {
  // Emit the event to mark the training as watched
  emit("markAsWatched", props.training.id);
}

const plyr = ref<any>();
const videoRef = ref<HTMLVideoElement | null>(null);
const hasSeekedToResumeTime = ref(false); // 1回だけセットするためのフラグ

function onReady() {
  // console.log("Plyr is ready");

  const player = plyr.value.player;

  if (!player) return;

  const watctedMinutes = props.training.myTrainingView.watchedMinutes || 0;
  const resumeSeconds = watctedMinutes * 60;

  let lastAllowedTime = resumeSeconds;

  const videoElement = videoRef.value;

  if (videoElement) {
    videoElement.addEventListener("loadedmetadata", () => {
      const duration = videoElement.duration || 0;

      if (!hasSeekedToResumeTime.value && duration > resumeSeconds) {
        player.currentTime = resumeSeconds;
        hasSeekedToResumeTime.value = true;
      }
    });
  }

  player.on("timeupdate", () => {
    const currentTime = player.currentTime;
    const duration = player.duration;
    const progress = currentTime / duration;

    if (progress >= 0.95 && !alreadyMarkedWatched.value) {
      markTrainingAsWatched();
      alreadyMarkedWatched.value = true;
    }

    if (player.currentTime - lastAllowedTime > 2) {
      lastAllowedTime = player.currentTime;
    }
  });

  /*
  player.on("seeking", () => {
    // 許可された時間より先に飛ぼうとしたら戻す
    if (player.currentTime > lastAllowedTime + 1) {
      player.currentTime = lastAllowedTime;
    }
  });
  */

  intervalId = setInterval(() => {
    // 小数点切り捨て
    const seconds = Math.floor(player.currentTime);
    const minutes = Math.floor(seconds / 60);

    // 5の倍数のときのみ送信（過去送信と重複しないようフラグで管理しても良い）
    if (minutes % 5 === 0 && minutes !== 0) {
      emit("sendWatchedMinutes", minutes);
    }
  }, 60000); // 60秒ごとに送信
}

onBeforeUnmount(() => {
  if (intervalId) clearInterval(intervalId);
});

// watcher

const selectedOrganizationId = ref<number | null>(
  props.training.organizations?.at(0)?.id
);
</script>

<style scoped></style>
